import { ArrowUpRightIcon } from "@phosphor-icons/react/dist/csr/ArrowUpRight";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CartesianGrid,
  Area,
  AreaChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@volca/ui/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@volca/ui/components/ui/card";
import { ChartContainer } from "@volca/ui/components/ui/chart";
import { Text } from "@volca/ui/components/ui/text";
import { dayjs } from "@/core/utils/date";
import { useReportCurrency } from "@/profitability/currency";
import { isNumericType } from "@/profitability/missing";
import { formatReportPeriod, formatSummaryValue } from "@/profitability/utils";
import { OverviewMetrics } from "./overview-metrics";
import type { OverviewFigure, OverviewHistoryPeriod } from "./types";

/** One script-declared headline at a time, using its saved values. */
export const OverviewPerformance = ({
  figures,
  history,
  insightsPath,
  compared,
}: {
  figures: OverviewFigure[];
  history: OverviewHistoryPeriod[];
  insightsPath: string;
  compared: boolean;
}) => {
  const currency = useReportCurrency();
  const [selected, setSelected] = useState<string | null>(null);
  const figure = figures.find((item) => item.key === selected) ?? figures[0];

  const data = history.map((period) => {
    const value = figure ? period.figures[figure.key] : null;
    return {
      period: formatReportPeriod(period),
      date: dayjs(period.start_date).format("MMM D"),
      value: period.comparable && typeof value === "number" ? value : null,
    };
  });
  const shown = data.filter((point) => point.value !== null);
  const compact = new Intl.NumberFormat(undefined, {
    notation: "compact",
    maximumFractionDigits: 1,
    ...(figure?.type === "currency" ? { style: "currency", currency } : {}),
  });
  const formatAxis = (value: number) =>
    figure?.type === "percent"
      ? `${Math.round(value * 100)}%`
      : compact.format(value);

  return (
    <Card
      className="@container/performance min-w-0"
      role="region"
      aria-label="Performance"
    >
      <CardHeader>
        <CardTitle>
          <h2>Performance</h2>
        </CardTitle>
        <CardAction>
          <Button
            className="pointer-coarse:min-h-11"
            variant="ghost"
            size="sm"
            nativeButton={false}
            render={<Link to={insightsPath} />}
          >
            Insights
            <ArrowUpRightIcon aria-hidden />
          </Button>
        </CardAction>
      </CardHeader>
      {figure && (
        <CardContent className="min-w-0">
          <OverviewMetrics
            figures={figures}
            selected={figure}
            onSelect={setSelected}
            compared={compared}
          />
        </CardContent>
      )}
      {figure && isNumericType(figure.type) && (
        <CardContent className="flex min-w-0 flex-col gap-3">
          <Text variant="caption" tone="muted">
            {figure.label} over time
          </Text>
          {shown.length < 2 ? (
            <Text variant="body" tone="muted" className="py-8">
              Two comparable periods are needed for a trend.
            </Text>
          ) : (
            <ChartContainer
              className="aspect-auto h-64 w-full"
              config={{ value: { label: figure.label, color: "var(--brand)" } }}
            >
              <AreaChart
                data={data}
                accessibilityLayer
                title={`${figure.label} over time`}
                margin={{ top: 12, right: 12, bottom: 0, left: 4 }}
              >
                <CartesianGrid
                  vertical={false}
                  stroke="var(--line)"
                  strokeOpacity={0.6}
                />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  minTickGap={32}
                  tick={{ fontSize: 12, fill: "var(--subtle)" }}
                />
                <YAxis
                  width={56}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={formatAxis}
                  tick={{ fontSize: 12, fill: "var(--subtle)" }}
                  domain={["auto", "auto"]}
                />
                <Tooltip
                  labelFormatter={(_, payload) => payload[0]?.payload.period}
                  formatter={(value) => [
                    formatSummaryValue(
                      { value: Number(value), type: figure.type },
                      currency,
                    ),
                    figure.label,
                  ]}
                  contentStyle={{
                    borderRadius: 8,
                    borderColor: "var(--line)",
                    backgroundColor: "var(--surface-raised)",
                    color: "var(--ink)",
                    fontSize: 12,
                  }}
                />
                <Area
                  dataKey="value"
                  stroke="var(--brand)"
                  fill="var(--brand)"
                  fillOpacity={0.06}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 4,
                    fill: "var(--brand)",
                    stroke: "var(--surface-raised)",
                    strokeWidth: 2,
                  }}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              </AreaChart>
            </ChartContainer>
          )}
          {data.some((point) => point.value === null) && (
            <Text variant="caption" tone="muted">
              Gaps mark missing data or a calculation change.
            </Text>
          )}
        </CardContent>
      )}
    </Card>
  );
};
